import { compact, isNull } from 'lodash';
import {
  UnAnalyzedSelectedExpression,
  Expr, Literal,
} from 'common/types/soql';
import { ViewColumn } from 'common/types/viewColumn';
import { buildColumnRef, buildLiteral, renderExpr } from './expr';
import {
  isString, isNumber, isViewColumn,
  QualifiedViewColumn, isQualifiedViewColumn
} from './util';

export interface Select {
  expr: QualifiedViewColumn | ViewColumn | Expr | Literal['value'] | number;
  name: string;
}

export interface PrerenderedExpression extends Select {
  type: 'prerendered-expression';
  expr: string;
}
export const isPrerenderedExpression = (x: Select | PrerenderedExpression | UnAnalyzedSelectedExpression): x is PrerenderedExpression => 'type' in x && x.type === 'prerendered-expression';
export const buildPrerenderedExpression = (expr: string, name: string): PrerenderedExpression => ({
  type: 'prerendered-expression',
  expr, name
});

/**
 * Construct a single SELECT object, expected to become a member of an array.
 *
 * @remarks
 * You should not try to do a SELECT *; it's a smell in custom-designed queries.
 *
 * @param expr An expression you wish to select.
 * @param name The alias for your selection.
 */
export const buildSelect = (expr: Select['expr'], name: string): Select => ({ expr, name });

export const convertSelect = (select: Select): UnAnalyzedSelectedExpression | PrerenderedExpression => {
  const name = { name: select.name, position: { line: 0, column: 0 } };
  let expr;

  if (isPrerenderedExpression(select)) {
    return select;
  } else if (isQualifiedViewColumn(select.expr)) {
    expr = buildColumnRef(select.expr.column, select.expr.qualifier);
  } else if (isViewColumn(select.expr)) {
    expr = buildColumnRef(select.expr);
  } else if (isString(select.expr) || isNumber(select.expr) || true === select.expr || false === select.expr || isNull(select.expr)) {
    expr = buildLiteral(select.expr);
  } else {
    expr = select.expr;
  }

  return { expr, name };
};

export const renderUnAnalyzedSelectedExpression = (expr: UnAnalyzedSelectedExpression | PrerenderedExpression): string => {
  const rendered = isPrerenderedExpression(expr) ? expr.expr : renderExpr(expr.expr);
  const alias = isPrerenderedExpression(expr) ? expr.name : expr.name?.name;

  return compact([rendered, alias]).join(' as ');
};

